// Home: timing curves, the intro's card choreography and the carousel's camera.
// Times in ms, sizes in design px (desktop 1680x1080, phone 402x874).

/** cubic-bezier(x1, 0, x2, 1)-style curves, control points read from Jitter */
function bezier(p: number, x1: number, x2: number) {
    if (p <= 0) return 0
    if (p >= 1) return 1
    let low = 0,
        high = 1,
        u = p
    for (let i = 0; i < 16; i++) {
        u = (low + high) / 2
        const x = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u
        if (x < p) low = u
        else high = u
    }
    return 3 * (1 - u) * u * u + u * u * u
}
export type Curve = "natural" | "opening" | "smooth" | "out" | "linear"
const CURVES: Record<Exclude<Curve, "linear">, [number, number]> = {
    opening: [0.88, 0.12],
    natural: [0.8, 0.2],
    smooth: [0.5, 0],
    out: [0, 0],
}
/** eased progress of a step that starts at `start` and lasts `duration` */
export function progress(t: number, start: number, duration: number, curve: Curve = "natural") {
    const p = Math.max(0, Math.min(1, (t - start) / duration))
    return curve === "linear" ? p : bezier(p, ...CURVES[curve])
}

/** The ten cards that fly in during the intro (five per side), from Jitter. */
export const CARDS = [
    { a: -40, from: 50, color: "#ffa500", move: 2000, flip: 2350, dur: 695, show: 1580 },
    { a: 45, from: -40, color: "#3100ff", move: 1992, flip: 2342, dur: 695, show: 1580 },
    { a: 25, from: -40, color: "#ff0096", move: 2169.5, flip: 2482.5, dur: 621, show: 2550 },
    { a: 25, from: -40, color: "#00ee9b", move: 2220, flip: 2533, dur: 621, show: 2640 },
    { a: 13, from: -40, color: "#ffffff", move: 2300, flip: 2483, dur: 621, show: 2720 },
]
/** the intro's last step; the slider takes over here */
export const SLIDER_START = Math.max(
    3045,
    ...CARDS.map((c, i) => Math.max(c.move + (i < 2 ? 1053 : 940), c.flip + c.dur, c.show + 90))
)
/** the whole intro is over (header, award) */
export const INTRO_END = 4160

// Published reference: 450x300 centre, 300x250 sides, gap 40, shared
// perspective 550, depth step 200 and 35° rotation per slot.
const referenceScale = 620 / 450
export const PERSPECTIVE = 550 * referenceScale

/** Within one slot of the centre, depth and size follow a smooth curve instead
 *  of a sharp V (no "click" as a tile passes the centre). */
function centreEase(a: number) {
    return a < 1 ? a * a * (2 - a) : a
}
export type Pose = { w: number; h: number; x: number; z: number; angle: number; r: number }
/** desktop: a card `offset` slots from the centre */
export function carouselPose(offset: number): Pose {
    const a = Math.abs(offset),
        sign = Math.sign(offset),
        d = centreEase(a),
        focus = Math.min(d, 1)
    return {
        w: (450 - 150 * focus) * referenceScale,
        h: (300 - 50 * focus) * referenceScale,
        x: sign * (a <= 1 ? 415 * a : 415 + (a - 1) * 289) * referenceScale,
        z: -d * 200 * referenceScale,
        angle: sign * Math.min(a * 35, 90),
        r: 22,
    }
}
/** phone: 370x432 centre card with 16px side margins; neighbours just off screen */
export function mobilePose(offset: number, cardH = 432): Pose {
    const a = Math.abs(offset),
        sign = Math.sign(offset),
        d = centreEase(a),
        focus = Math.min(d, 1)
    return {
        w: 370 - 70 * focus,
        h: cardH - 82 * (cardH / 432) * focus,
        x: sign * (a <= 1 ? 420 * a : 420 + (a - 1) * 300),
        z: -d * 200 * referenceScale,
        angle: sign * Math.min(a * 35, 90),
        r: 16,
    }
}

/** Per-frame style writes skip unchanged values (no needless style recalc). */
const cache = new WeakMap<HTMLElement, Record<string, string>>()
export function put(el: HTMLElement | undefined | null, prop: string, value: string) {
    if (!el) return
    let c = cache.get(el)
    if (!c) cache.set(el, (c = {}))
    if (c[prop] === value) return
    c[prop] = value
    ;(el.style as any)[prop] = value
}
